import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  GppGood as SignIcon,
  WhatsApp as WhatsAppIcon,
  ScheduleSend as ScheduledIcon,
} from '@mui/icons-material';
import SignPrescriptionDialog from '../clinic/SignPrescriptionDialog';
import SendDocumentDialog from '../SendDocumentDialog';
import { isMemedPrescription } from '../clinic/MemedPrescriptionBadge';
import type { BatchSignResult, SignedDocumentInfo } from '../../services/documentSignatureService';
import { fetchLinkedPrescription } from '../../utils/evolutionPrescriptionPrint';
import {
  hasShareablePrescriptionPdf,
  isSignedPrescription,
  notifyPrescriptionsSigned,
} from '../../utils/prescriptionSignature';
import { prescriptionTypeLabel } from '../../utils/prescriptionTypes';

const MAX_PER_LINK = 20;

export type EvolutionPrescriptionsSendMode = 'pick' | 'sign_then_send';

interface Props {
  open: boolean;
  onClose: () => void;
  prescriptionIds: string[];
  patientId?: string | null;
  /**
   * `pick`: lista para escolher, assinar e enviar.
   * `sign_then_send`: assina as pendentes direto e abre o envio ao final (fluxo "Salvar e assinar").
   */
  mode?: EvolutionPrescriptionsSendMode;
  onNotify?: (message: string, severity: 'success' | 'error') => void;
}

type Step = 'list' | 'sign' | 'send' | null;

function needsIcpSignature(rx: any): boolean {
  return Boolean(rx?.id) && rx.status !== 'cancelled' && !isMemedPrescription(rx) && !isSignedPrescription(rx);
}

function formatIssueDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
}

const EvolutionPrescriptionsSendDialog: React.FC<Props> = ({
  open,
  onClose,
  prescriptionIds,
  patientId,
  mode = 'pick',
  onNotify,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<Step>(null);
  const [sendAfterSign, setSendAfterSign] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idsKey = prescriptionIds.join(',');
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async (): Promise<any[]> => {
    const ids = idsKey ? idsKey.split(',') : [];
    const loaded = await Promise.all(ids.map((id) => fetchLinkedPrescription(id)));
    const valid = loaded.filter((rx) => rx?.id && rx.status !== 'cancelled');
    valid.sort((a, b) => String(a.issue_date || '').localeCompare(String(b.issue_date || '')));
    setRows(valid);
    return valid;
  }, [idsKey]);

  useEffect(() => {
    if (!open) {
      setStep(null);
      return;
    }
    let cancelled = false;
    setError(null);
    setLoading(true);
    void load()
      .then((valid) => {
        if (cancelled) return;
        setSelected(new Set(valid.map((rx) => rx.id)));
        if (mode === 'pick') {
          setStep('list');
          return;
        }
        if (valid.some(needsIcpSignature)) {
          setSendAfterSign(true);
          setStep('sign');
        } else if (valid.length) {
          setStep('send');
        } else {
          onCloseRef.current();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, load, mode]);

  const selectedRows = useMemo(() => rows.filter((rx) => selected.has(rx.id)), [rows, selected]);
  const pendingSelected = useMemo(() => selectedRows.filter(needsIcpSignature), [selectedRows]);
  const signTargets = step === 'sign'
    ? (mode === 'pick' ? pendingSelected : rows.filter(needsIcpSignature))
    : [];
  const sendIds = useMemo(() => selectedRows.map((rx) => rx.id as string), [selectedRows]);
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const first = rows[0];

  const finish = () => {
    setStep(null);
    onClose();
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const validateSelection = (forSigning: boolean): boolean => {
    if (!selectedRows.length) {
      setError('Selecione pelo menos uma receita.');
      return false;
    }
    if (selectedRows.length > MAX_PER_LINK) {
      setError(`Envie no máximo ${MAX_PER_LINK} receitas por vez.`);
      return false;
    }
    if (forSigning) {
      if (pendingSelected.some((rx) => !rx.professional_id)) {
        setError('Defina o profissional prescritor nas receitas antes de assinar.');
        return false;
      }
      if (new Set(pendingSelected.map((rx) => rx.professional_id)).size > 1) {
        setError('Assine juntas apenas receitas do mesmo prescritor.');
        return false;
      }
    }
    setError(null);
    return true;
  };

  const startSign = (thenSend: boolean) => {
    if (!validateSelection(true)) return;
    setSendAfterSign(thenSend);
    setStep('sign');
  };

  const startSend = () => {
    if (!validateSelection(false)) return;
    setStep('send');
  };

  const afterSigning = async (signedIds: string[], failedMessage?: string) => {
    notifyPrescriptionsSigned(signedIds);
    if (failedMessage) onNotify?.(failedMessage, 'error');
    else if (signedIds.length) {
      onNotify?.(
        signedIds.length > 1
          ? `${signedIds.length} receitas assinadas com uma única autorização.`
          : 'Receita assinada digitalmente.',
        'success',
      );
    }
    const fresh = await load();
    if (sendAfterSign && fresh.length) {
      setStep('send');
      return;
    }
    if (mode === 'pick') setStep('list');
    else finish();
  };

  const handleBatchSigned = (result: BatchSignResult) => {
    const failed = result.failed.length
      ? `${result.failed.length} receita(s) não assinada(s): ${result.failed[0].error}`
      : undefined;
    void afterSigning(result.signed.map((s) => s.id), failed);
  };

  const handleSingleSigned = (info: SignedDocumentInfo) => {
    const id = info?.id || signTargets[0]?.id;
    void afterSigning(id ? [id] : []);
  };

  const closeSign = () => {
    if (mode === 'pick') setStep('list');
    else finish();
  };

  const closeSend = () => {
    if (mode === 'pick') setStep('list');
    else finish();
  };

  return (
    <>
      <Dialog
        open={open && step === 'list'}
        onClose={finish}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Enviar receitas ao paciente</Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {first?.patient_name ? `${first.patient_name} · ` : ''}
              As selecionadas vão juntas, num link só pelo WhatsApp.
            </Typography>
          </Box>
          <IconButton onClick={finish} size="small"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ p: 3, color: '#64748b' }}>Nenhuma receita encontrada nesta evolução.</Typography>
          ) : (
            <>
              <Box
                sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selected.size > 0 && !allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((rx) => rx.id)))}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                  {allSelected ? 'Todas selecionadas' : `${selected.size} de ${rows.length} selecionada(s)`}
                </Typography>
              </Box>
              {rows.map((rx) => {
                const signed = isSignedPrescription(rx);
                const memed = isMemedPrescription(rx);
                const itemsCount = Array.isArray(rx.items) ? rx.items.length : 0;
                const date = formatIssueDate(rx.issue_date);
                const isFuture = rx.issue_date && String(rx.issue_date).slice(0, 10) > new Date().toISOString().slice(0, 10);
                return (
                  <Box
                    key={rx.id}
                    onClick={() => toggle(rx.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1,
                      borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    <Checkbox size="small" checked={selected.has(rx.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggle(rx.id)} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }} noWrap>
                        {rx.prescription_number || 'Receituário'} · {prescriptionTypeLabel(rx.prescription_type)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isFuture && <ScheduledIcon sx={{ fontSize: 14 }} />}
                        {date ? `${isFuture ? 'Programada para ' : ''}${date}` : ''}
                        {itemsCount ? ` · ${itemsCount} medicamento(s)` : ''}
                      </Typography>
                    </Box>
                    {signed ? (
                      <Chip size="small" label="Assinada" icon={<SignIcon sx={{ fontSize: '0.85rem !important' }} />}
                        sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, '& .MuiChip-icon': { color: '#16a34a' } }} />
                    ) : memed ? (
                      <Chip size="small" label={hasShareablePrescriptionPdf(rx) ? 'Memed' : 'Memed (sem PDF)'} variant="outlined" />
                    ) : (
                      <Chip size="small" label="Pendente de assinatura" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
                    )}
                  </Box>
                );
              })}
            </>
          )}
          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={finish} sx={{ textTransform: 'none', color: '#64748b' }}>Fechar</Button>
          <Box sx={{ flex: 1 }} />
          {pendingSelected.length > 0 && (
            <Button
              variant="outlined"
              onClick={() => startSign(false)}
              startIcon={<SignIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Só assinar ({pendingSelected.length})
            </Button>
          )}
          {pendingSelected.length > 0 && (
            <Button
              variant="text"
              onClick={startSend}
              disabled={!selectedRows.length}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Enviar sem assinar
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            disabled={!selectedRows.length || loading}
            onClick={() => (pendingSelected.length ? startSign(true) : startSend())}
            startIcon={pendingSelected.length ? <SignIcon fontSize="small" /> : <WhatsAppIcon fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
          >
            {pendingSelected.length
              ? `Assinar e enviar (${selectedRows.length})`
              : `Enviar ao paciente (${selectedRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <SignPrescriptionDialog
        open={open && step === 'sign' && signTargets.length > 0}
        prescription={signTargets.length === 1 ? signTargets[0] : null}
        documents={signTargets.length > 1 ? signTargets : null}
        onClose={closeSign}
        onSigned={handleSingleSigned}
        onBatchSigned={handleBatchSigned}
      />

      <SendDocumentDialog
        open={open && step === 'send' && sendIds.length > 0}
        onClose={closeSend}
        documentType="prescription"
        documentId={sendIds[0] || ''}
        documentIds={sendIds.length > 1 ? sendIds : undefined}
        patientId={first?.patient_id || patientId || undefined}
        patientEmail={first?.patient_email}
        patientPhone={first?.patient_phone}
        patientWhatsapp={first?.patient_whatsapp}
        patientName={first?.patient_name}
        documentLabel={sendIds.length > 1 ? 'Receitas' : 'Receituário'}
      />
    </>
  );
};

export default EvolutionPrescriptionsSendDialog;
